import { DirectoryInput, Input, SelectInput, useDialog } from "@repo/ui";
import type { Folder } from "~/types/api";
import type { TaskManagerDTO } from "~/types/api-custom";
import { TaskDialogFooter } from "../task-dialog-footer";
import { useCreateTask } from "../use-create-task";
import { useUpdateTask } from "../use-update-task";
import { TaskFieldInput } from "./task-field-input";
import { useTaskForm } from "./use-task-form";

type Props = {
  task?: TaskManagerDTO;
  onClose?: () => void;
};

const toId = (val: string | string[] | undefined) => {
  const v = Array.isArray(val) ? val[0] : val;
  return v == null || v === "" ? undefined : Number(v);
};

export const TaskForm = ({ task, onClose }: Props) => {
  const { showDialog, closeDialog } = useDialog();
  const { handleCreate, isPending: isCreating } = useCreateTask();
  const { handleUpdate, isPending: isUpdating } = useUpdateTask();

  const {
    isEditMode,
    formData,
    errors,
    folders,
    selectedBrandId,
    selectedWorkflowId,
    currentFolderId,
    workflowFields,
    isLoadingFields,
    originalFolderId,
    hasChanges,
    isFormValid,
    validateOnSubmit,
    clearError,
    brandOptions,
    workflowOptions,
    folderOptions,
    updateFormField,
    updateTaskFieldValue,
    getTaskFieldValue,
    getTaskFieldValueAsArray,
    isCheckingPath,
  } = useTaskForm(task);

  const isPending = isCreating || isUpdating;

  const handleOnSave = async (e?: React.FormEvent) => {
    e?.preventDefault();

    if (!validateOnSubmit()) return;

    try {
      if (isEditMode && task?.id) {
        const updateData = {
          ...formData,
          taskFields: isLoadingFields
            ? formData.taskFields
            : workflowFields?.length > 0
              ? formData.taskFields
              : undefined,
        };

        await handleUpdate(task.id, updateData as TaskManagerDTO, originalFolderId);
      } else {
        await handleCreate(formData as TaskManagerDTO);
      }
    } catch (error) {
      // Error toast and rollback are handled in the mutation's onError.
      console.error("Failed to save task:", error);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      showDialog?.({
        title: "Unsaved Changes",
        description: "You have unsaved changes. Are you sure you want to cancel without saving?",
        cancelText: "Keep Editing",
        confirmText: "Discard Changes",
        confirmVariant: "destructive",
        onConfirm: () => {
          closeDialog();
          onClose?.();
        },
      });
    } else {
      onClose?.();
    }
  };

  return (
    <form onSubmit={handleOnSave} className="flex flex-col gap-4">
      {!isEditMode && (
        <SelectInput
          label="Brand"
          options={brandOptions}
          placeholder="Select Brand"
          value={formData.brandId?.toString()}
          onChange={(val) => {
            clearError("brandId");
            const value = toId(val);
            updateFormField("brandId", value);
            updateFormField("workflowId", undefined);
            updateFormField("folder", undefined);
          }}
          error={errors.brandId}
          showClearIcon
          required
        />
      )}

      <SelectInput
        label="Task Folder"
        options={folderOptions}
        placeholder={selectedBrandId ? "Select Folder" : "Select a brand first"}
        value={currentFolderId?.toString()}
        onChange={(val) => {
          const value = toId(val);
          const selectedFolder = folders?.find((f: Folder) => f.id === value);
          updateFormField(
            "folder",
            selectedFolder
              ? {
                  id: selectedFolder.id,
                  name: selectedFolder.name,
                }
              : undefined
          );
        }}
        disabled={!selectedBrandId}
        showClearIcon
      />

      <Input
        label="Task Name"
        required
        placeholder="Enter task name"
        value={formData.name}
        onChange={(e) => {
          clearError("name");
          updateFormField("name", e.target.value);
        }}
        error={errors.name}
        showClearIcon
      />

      <SelectInput
        label="Workflow"
        options={workflowOptions}
        placeholder={!isEditMode && !selectedBrandId ? "Select a brand first" : "Select Workflow"}
        value={formData.workflowId?.toString()}
        onChange={(val) => {
          clearError("workflowId");
          const value = toId(val);
          updateFormField("workflowId", value);
        }}
        error={errors.workflowId}
        required
        disabled={!isEditMode && !selectedBrandId}
        showClearIcon
      />

      <DirectoryInput
        apiBaseUrl={import.meta.env.VITE_FS_API_URL}
        label="Folder Path"
        required
        placeholder="Select a folder..."
        value={formData.path}
        onChange={(newPath: string | null) => {
          clearError("path");
          updateFormField("path", newPath ?? "");
        }}
        error={errors.path}
        helperText={isCheckingPath ? "Checking folder..." : undefined}
        showClearIcon
      />

      {!!selectedWorkflowId && (
        <>
          {isLoadingFields ? (
            <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
              Loading workflow fields...
            </div>
          ) : workflowFields.length > 0 ? (
            <div className="space-y-4 rounded-md border border-blue-200 bg-blue-50 p-4">
              <h4 className="text-sm font-semibold text-blue-900">Metadata Fields</h4>
              {workflowFields.map((field) => (
                <TaskFieldInput
                  key={field.id}
                  field={field}
                  value={getTaskFieldValue(field.id)}
                  valueAsArray={getTaskFieldValueAsArray(field.id)}
                  onValueChange={(fieldId, value, type) => {
                    clearError(`taskField-${fieldId}`);
                    updateTaskFieldValue(fieldId, value, type);
                  }}
                  error={errors.taskFields?.[field.id]}
                />
              ))}
            </div>
          ) : null}
        </>
      )}
      <TaskDialogFooter
        loading={isPending}
        onSave={handleOnSave}
        onCancel={handleCancel}
        disabled={
          isEditMode
            ? !hasChanges || !isFormValid || isLoadingFields
            : !isFormValid || isLoadingFields
        }
      />
    </form>
  );
};
